import React, { useState, useRef, useEffect } from 'react';
import {
  Box, TextField, Button, Typography, IconButton, Drawer, CircularProgress,
  Tooltip, Divider, useTheme, useMediaQuery, AppBar, Toolbar, Paper, InputBase,
  ClickAwayListener, Card, CardContent, Chip, Snackbar, Alert, Badge, Dialog,
  DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Mic as MicIcon, AutoFixHigh as MagicIcon, Save as SaveIcon, ArrowBack as BackIcon,
  Close as CloseIcon, Search as SearchIcon, MenuBook as DictionaryIcon,
  Lightbulb as IdeaIcon, ContentCopy as CopyIcon, Check as CheckIcon,
  ChevronRight as ChevronRightIcon, Edit as EditIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import apiClient from '../services/apiClient';
import LibraryBrowser from './LibraryBrowser';
import WhisperRecording from './WhisperRecording';
import AddToVocabButton from './AddToVocabButton';
import { JOURNAL_MOODS } from './journalMoods';
import { useTutorContext } from '../context/TutorContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const JournalEditor = ({ targetLanguage, onSave, onCancel, initialData, initialContent }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Friendly display name (e.g. "French") for the language code ("fr").
  const { getLanguageDisplayName } = useLanguage();
  const languageName = getLanguageDisplayName ? getLanguageDisplayName(targetLanguage) : targetLanguage;

  // Content state. `initialContent` (e.g. a tutor "add to journal" draft) seeds a
  // brand-new entry's editable text without going through `initialData` (which is
  // reserved for editing an existing entry).
  const [content, setContent] = useState(initialData?.raw_content || initialContent || '');
  const [title, setTitle] = useState(initialData?.title || '');
  const [cursorPosition, setCursorPosition] = useState(0);

  // Analysis state
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(initialData?.analysis_data || null);
  const [hasBeenPolished, setHasBeenPolished] = useState(!!initialData?.analysis_data);
  // The exact text the analysis ran on. Polish swaps the editor text for the
  // corrected version, so without this the learner's original was lost on save.
  const [analyzedText, setAnalyzedText] = useState(null);
  // Mood: the analysis suggests one unless the learner has already picked.
  const [mood, setMood] = useState(initialData?.mood || null);
  const [moodTouched, setMoodTouched] = useState(!!initialData?.mood);

  // Dictionary state
  const [showDictionary, setShowDictionary] = useState(false);
  const [dictQuery, setDictQuery] = useState('');
  const [dictResult, setDictResult] = useState(null);
  const [dictLoading, setDictLoading] = useState(false);

  // Corrections sidebar state
  const [correctionsDrawerOpen, setCorrectionsDrawerOpen] = useState(false);

  // Correction detail modal
  const [selectedCorrection, setSelectedCorrection] = useState(null);
  const [correctionModalOpen, setCorrectionModalOpen] = useState(false);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);

  // Topic library browser ("Browse topics" modal)
  const [libraryOpen, setLibraryOpen] = useState(false);

  // Use a chosen library topic as the entry's starting point. Mirrors the
  // "writing prompt" behavior: seed the title if the entry is blank, else
  // insert at the cursor.
  const handleLibrarySelect = (item) => {
    const topic = item.body || item.title;
    if (!title && !content) {
      setTitle(topic);
    } else {
      insertTextAtCursor(topic);
    }
    showNotification('Topic added — start writing!', 'success');
  };

  // Notification state
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const contentRef = useRef(null);

  // Publish journal context to the floating tutor so it can help write/fix sentences.
  const { setTutorContext, clearTutorContext } = useTutorContext();
  const { user } = useAuth();
  useEffect(() => {
    const lang = user?.languages?.find(l => l.language_code === targetLanguage);
    setTutorContext({
      page: 'journal',
      draft: content,
      proficiency_level: lang?.proficiency_level || null,
    });
  }, [content, targetLanguage, user, setTutorContext]);
  useEffect(() => () => clearTutorContext(), [clearTutorContext]);

  const showNotification = (message, severity = 'info') => {
    setNotification({ open: true, message, severity });
  };

  const insertTextAtCursor = (textToInsert) => {
    const newContent =
      content.substring(0, cursorPosition) +
      (content.length > 0 && cursorPosition > 0 && content[cursorPosition - 1] !== ' ' ? ' ' : '') +
      textToInsert +
      content.substring(cursorPosition);
    setContent(newContent);
    const newPos = cursorPosition + textToInsert.length + 1;
    setCursorPosition(newPos);

    if (contentRef.current) {
      setTimeout(() => {
        contentRef.current.selectionStart = newPos;
        contentRef.current.selectionEnd = newPos;
        contentRef.current.focus();
      }, 50);
    }
  };

  // Polish the entry - replaces content with corrected version
  const handlePolish = async () => {
    if (!content.trim()) {
      showNotification('Please write something first', 'warning');
      return;
    }
    setIsAnalyzing(true);
    try {
      const response = await apiClient.journal.analyzeEntry(content, targetLanguage);
      setAnalysis(response.data);
      setAnalyzedText(content);
      if (!moodTouched && response.data?.mood) setMood(response.data.mood);

      // Replace content with polished version
      if (response.data.polished_content) {
        setContent(response.data.polished_content);
      }

      setHasBeenPolished(true);

      // Show corrections count
      const correctionsCount = response.data.segments?.filter(s => s.correction).length || 0;
      if (correctionsCount > 0) {
        showNotification(`Polished! ${correctionsCount} correction${correctionsCount > 1 ? 's' : ''} made. Tap the badge to review.`, 'success');
      } else {
        showNotification('Perfect! No corrections needed.', 'success');
      }
    } catch (error) {
      console.error("Analysis failed", error);
      showNotification('Failed to analyze entry. Please try again.', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Generate a writing prompt
  const handleGeneratePrompt = async () => {
    try {
      const response = await apiClient.journal.getPrompt(targetLanguage);
      if (!title && !content) {
        setTitle(response.data.prompt_text);
      } else {
        insertTextAtCursor(response.data.prompt_text);
      }
      showNotification('Writing prompt added!', 'success');
    } catch (error) {
      console.error("Prompt generation failed", error);
      showNotification('Failed to generate prompt', 'error');
    }
  };

  // Dictionary lookup
  const handleDictionaryLookup = async (e) => {
    e.preventDefault();
    if (!dictQuery.trim()) return;
    setDictLoading(true);
    try {
      const response = await apiClient.journal.lookupWord(dictQuery, 'en', targetLanguage);
      setDictResult(response.data);
    } catch (error) {
      console.error("Lookup failed", error);
      setDictResult(null);
      showNotification('Word not found', 'warning');
    } finally {
      setDictLoading(false);
    }
  };

  // Handle Whisper transcription
  const handleTranscriptionComplete = (transcription, audioId) => {
    if (transcription && transcription.trim()) {
      insertTextAtCursor(transcription);
      showNotification('Voice transcription added!', 'success');
    }
    setIsRecording(false);
  };

  const handleRecordingStateChange = (recording) => {
    setIsRecording(recording);
  };

  // Save the entry
  const handleSave = () => {
    // raw_content is the text the analysis ran on, so the saved original lines
    // up with the corrections (and the entry view can highlight what changed).
    const rawContent = analysis
      ? (analyzedText ?? initialData?.raw_content ?? content)
      : content;
    onSave({
      title: title || 'Untitled Entry',
      raw_content: rawContent,
      polished_content: content,
      analysis_data: analysis,
      mood,
      tags: ['Journal'],
      glossary: analysis?.vocabulary || []
    });
  };

  // Open correction detail
  const handleCorrectionClick = (correction) => {
    setSelectedCorrection(correction);
    setCorrectionModalOpen(true);
  };

  // Get corrections count
  const correctionsCount = analysis?.segments?.filter(s => s.correction).length || 0;

  // Room under the text for the fixed toolbar (~64px) + tutor button, on top of
  // the mobile bottom nav's real height (published by Layout; 0 when no nav).
  const bottomSpacing = 'calc(var(--bottom-nav-height, 0px) + 96px)';

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',

      bgcolor: 'background.paper'
    }}>
      {/* Header */}
      <AppBar position="sticky" color="inherit" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={onCancel}>
            <BackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ ml: 1, flex: 1 }} noWrap>
            {initialData ? 'Edit Entry' : 'New Entry'}
          </Typography>

          {/* Corrections badge - opens sidebar */}
          {hasBeenPolished && correctionsCount > 0 && (
            <Tooltip title="View corrections">
              <IconButton
                onClick={() => setCorrectionsDrawerOpen(true)}
                color="secondary"
                sx={{ mr: 1 }}
              >
                <Badge badgeContent={correctionsCount} color="error">
                  <MagicIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          )}

          {/* Save button - always visible */}
          <Button
            variant="contained"
            color="success"
            onClick={handleSave}
            disabled={!content.trim()}
            startIcon={<SaveIcon />}
            size={isMobile ? "small" : "medium"}
          >
            Save
          </Button>
        </Toolbar>
      </AppBar>

      {/* Helper text */}
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Write in {languageName}. Don't know a word? Just write it in English!
        </Typography>
      </Box>

      {/* Editor content */}
      <Box sx={{
        flex: 1,
        overflow: 'auto',
        px: 2,
        pb: bottomSpacing
      }}>
        <TextField
          fullWidth
          variant="standard"
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          InputProps={{ disableUnderline: true }}
          sx={{
            mb: 2,
            '& input': {
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              fontWeight: 600
            }
          }}
        />
        {/* Mood — suggested by Polish, or picked here (tap again to clear) */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.75, mb: 2,
          flexWrap: isMobile ? 'nowrap' : 'wrap', overflowX: isMobile ? 'auto' : 'visible', pb: isMobile ? 0.5 : 0
        }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, flexShrink: 0 }}>
            Mood
          </Typography>
          {JOURNAL_MOODS.map((m) => (
            <Chip
              key={m.value}
              size="small"
              label={`${m.emoji} ${m.label}`}
              variant={mood === m.value ? 'filled' : 'outlined'}
              color={mood === m.value ? 'primary' : 'default'}
              onClick={() => { setMoodTouched(true); setMood(mood === m.value ? null : m.value); }}
              sx={{ flexShrink: 0 }}
            />
          ))}
        </Box>
        <TextField
          fullWidth
          multiline
          variant="standard"
          placeholder={`Start writing in ${languageName}...`}
          value={content}
          onChange={(e) => {
            setContent(e.target.value);
            if (hasBeenPolished) {
              setHasBeenPolished(false);
              setAnalysis(null);
              setAnalyzedText(null);
            }
          }}
          onSelect={(e) => setCursorPosition(e.target.selectionStart)}
          onClick={(e) => setCursorPosition(e.target.selectionStart)}
          onKeyUp={(e) => setCursorPosition(e.target.selectionStart)}
          InputProps={{ disableUnderline: true }}
          inputRef={contentRef}
          minRows={12}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: isMobile ? '1rem' : '1.1rem',
              lineHeight: 1.8
            }
          }}
        />
      </Box>

      {/* Dictionary popup */}
      {showDictionary && (
        <ClickAwayListener onClickAway={() => setShowDictionary(false)}>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              top: 70,
              left: 16,
              right: 16,
              maxWidth: 360,
              zIndex: 1300,
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Box
              component="form"
              onSubmit={handleDictionaryLookup}
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider'
              }}
            >
              <SearchIcon color="action" sx={{ mr: 1 }} />
              <InputBase
                autoFocus
                placeholder="Type English word..."
                value={dictQuery}
                onChange={(e) => setDictQuery(e.target.value)}
                sx={{ flex: 1 }}
              />
              <IconButton
                size="small"
                onClick={() => {
                  setShowDictionary(false);
                  setDictQuery('');
                  setDictResult(null);
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            {dictLoading ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            ) : dictResult ? (
              <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {languageName.toUpperCase()} TRANSLATION
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {dictResult.translation}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  fullWidth
                  sx={{ color: 'white', borderColor: 'white', mt: 1.5 }}
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    insertTextAtCursor(dictResult.translation);
                    setShowDictionary(false);
                    setDictQuery('');
                    setDictResult(null);
                  }}
                >
                  Insert
                </Button>
                <AddToVocabButton
                  word={dictResult.translation}
                  translation={dictResult.original || dictQuery}
                  languageCode={targetLanguage}
                  fullWidth
                  variant="outlined"
                  sx={{ color: 'white', borderColor: 'white', mt: 1 }}
                />
              </Box>
            ) : (
              <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body2">
                  Type a word and press Enter
                </Typography>
              </Box>
            )}
          </Paper>
        </ClickAwayListener>
      )}

      {/* Bottom toolbar */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          // Sit on top of the mobile bottom nav at its real height (published by Layout).
          bottom: 'var(--bottom-nav-height, 0px)',
          left: 0,
          right: 0,
          p: 1.5,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1.5,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1100
        }}
      >
        {/* Dictionary button */}
        <Tooltip title="Look up words">
          <IconButton
            onClick={() => setShowDictionary(!showDictionary)}
            color={showDictionary ? 'primary' : 'default'}
            sx={{ bgcolor: 'action.hover' }}
          >
            <DictionaryIcon />
          </IconButton>
        </Tooltip>

        {/* Inspiration button — generate a fresh AI prompt */}
        <Tooltip title="Get writing prompt">
          <IconButton
            onClick={handleGeneratePrompt}
            color="secondary"
            sx={{ bgcolor: 'action.hover' }}
          >
            <IdeaIcon />
          </IconButton>
        </Tooltip>

        {/* Browse topics — pick from the curated library */}
        <Tooltip title="Browse topics">
          <IconButton
            onClick={() => setLibraryOpen(true)}
            color="secondary"
            sx={{ bgcolor: 'action.hover' }}
          >
            <AutoAwesomeIcon />
          </IconButton>
        </Tooltip>

        {/* Microphone button */}
        <WhisperRecording
          targetLanguage={targetLanguage}
          onTranscriptionComplete={handleTranscriptionComplete}
          onRecordingStateChange={handleRecordingStateChange}
        />

        {/* Polish button - single, clean */}
        <Button
          variant="contained"
          onClick={handlePolish}
          disabled={!content.trim() || isAnalyzing}
          sx={{ minWidth: 100 }}
        >
          {isAnalyzing ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <>
              <MagicIcon sx={{ mr: isMobile ? 0 : 1 }} />
              {!isMobile && 'Polish'}
            </>
          )}
        </Button>
      </Paper>

      {/* Corrections Sidebar Drawer */}
      <Drawer
        anchor="right"
        open={correctionsDrawerOpen}
        onClose={() => setCorrectionsDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: isMobile ? '90%' : 380,
            maxWidth: '100%'
          }
        }}
      >
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MagicIcon color="secondary" />
              What Changed
            </Typography>
            <IconButton onClick={() => setCorrectionsDrawerOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {analysis?.feedback && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {analysis.feedback}
            </Typography>
          )}
          {analysis?.fluency_score != null && (
            <Typography variant="body2" sx={{ mt: 1, fontWeight: 600 }}>
              Fluency: {analysis.fluency_score}/100
            </Typography>
          )}
        </Box>

        {correctionsCount === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="body1">Perfect! No corrections needed.</Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {analysis?.segments?.filter(s => s.correction).map((item, index) => (
              <ListItem
                key={index}
                button
                onClick={() => handleCorrectionClick(item)}
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.type === 'vocabulary' || item.type === 'native' ? (
                    <IdeaIcon color="secondary" />
                  ) : (
                    <EditIcon color="primary" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                      >
                        {item.text}
                      </Typography>
                      <Typography variant="body1" fontWeight="medium" color="primary">
                        {item.correction}
                      </Typography>
                    </Box>
                  }
                  secondary={
                    item.type === 'vocabulary' || item.type === 'native'
                      ? '✨ New vocabulary!'
                      : '📝 Grammar tip'
                  }
                />
                <ChevronRightIcon color="action" />
              </ListItem>
            ))}
          </List>
        )}

        {/* Vocabulary section */}
        {analysis?.vocabulary && analysis.vocabulary.length > 0 && (
          <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              KEY VOCABULARY
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {analysis.vocabulary.map((word, index) => (
                <Chip
                  key={index}
                  label={`${word.word} - ${word.translation}`}
                  size="small"
                />
              ))}
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Correction Detail Modal */}
      <Dialog
        open={correctionModalOpen}
        onClose={() => setCorrectionModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        {selectedCorrection && (
          <>
            <DialogTitle sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: (selectedCorrection.type === 'vocabulary' || selectedCorrection.type === 'native')
                ? 'secondary.light'
                : 'primary.light'
            }}>
              {(selectedCorrection.type === 'vocabulary' || selectedCorrection.type === 'native') ? (
                <IdeaIcon color="secondary" />
              ) : (
                <EditIcon color="primary" />
              )}
              {(selectedCorrection.type === 'vocabulary' || selectedCorrection.type === 'native')
                ? '✨ New Word Learned!'
                : '📝 Grammar Tip'}
            </DialogTitle>
            <DialogContent sx={{ pt: 3 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  YOU WROTE
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ bgcolor: 'grey.100', p: 1.5, borderRadius: 1 }}
                >
                  {selectedCorrection.text}
                </Typography>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="success.main" display="block">
                  CORRECTED TO
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    color: 'success.main',
                    bgcolor: 'success.light',
                    p: 1.5,
                    borderRadius: 1
                  }}
                >
                  {selectedCorrection.correction}
                </Typography>
              </Box>

              {selectedCorrection.explanation && (
                <Box sx={{
                  p: 2,
                  bgcolor: 'info.light',
                  borderRadius: 1,
                  borderLeft: '4px solid',
                  borderColor: 'info.main'
                }}>
                  <Typography variant="body2">
                    💡 {selectedCorrection.explanation}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <AddToVocabButton
                word={selectedCorrection.correction}
                translation={selectedCorrection.text}
                context={selectedCorrection.explanation}
                languageCode={targetLanguage}
                maxWords={3}
              />
              <Button onClick={() => setCorrectionModalOpen(false)}>
                Got it!
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Topic library browser */}
      <LibraryBrowser
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        kind="journal"
        onSelect={handleLibrarySelect}
        heading="Browse writing topics"
      />

      {/* Notification */}
      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={notification.severity}
          variant="filled"
          onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default JournalEditor;